// @ts-check
// Cliente de servicio de autocorrección server-validada de ejercicios de
// diagrama (BLK-01-DIAGRAMS, TSK-0123, capa services de docs/03-frontend.md
// §4.2/§9.1). Como todo servicio, no toca el DOM, no importa nada de
// features/**, components/** ni pages/**, y usa exclusivamente `apiRequest`
// de `api-client.js` -- nunca `fetch` directamente. El componente/página que
// lo invoque (TSK-0124) NO debe llamar a `fetch`/`apiRequest` por su cuenta:
// pasa siempre por `autocorregirDiagrama`.
//
// Consume `POST /api/v1/exercises/{ejercicio_id}/autocorrect` (TSK-0122,
// backend, ya en producción). Es una mutación autenticada por cookie de
// sesión (`credentials: 'include'`, mismo valor que usan todas las llamadas
// de `auth-client.js`) y exige CSRF (`X-CSRF-Token`, `verify_csrf`): mismo
// patrón EXACTO que `auth-client.js#cerrarSesion` -- el token se recibe
// como parámetro
// explícito (`opciones.csrfToken`), nunca se lee `session-store.js`
// directamente desde un service (los services no gestionan estado, solo
// hablan con la red; ese acoplamiento es responsabilidad de quien llame a
// esta función, igual que en los dos módulos citados).
//
// El body enviado es `{ programa }`, con `programa` la IR-JSON producida por
// `frontend/src/features/diagram-editor/ir-serialization.js#serializarPrograma`
// tal cual (misma forma que consume `diagram-execution-client.js#ejecutarDiagrama`
// para `POST /diagrams/execute`; se valida aquí con el mismo criterio
// defensivo -- `TypeError` síncrono si no es un objeto plano -- antes de
// llamar a `apiRequest` ni una sola vez).
//
// Contrato de respuesta 200 (`ResultadoAutocorreccion`, snake_case en la
// red): `{ correcto: bool, completado: bool, intentos: int, casos:
// [{entrada: str, salida_esperada: str, salida_obtenida: str, correcto:
// bool}], puntuacion_obtenida: int | None, penalizacion_pista: int,
// duracion_segundos: int | None }`. Los tres campos de texto de cada caso son
// `str` en el schema, NO listas (ver el typedef de `CasoAutocorreccion`). Los
// tres últimos campos son nuevos (DEC-065, TSK-0176): materializan la
// penalización de pista (DEC-063 punto 3) y el cierre server-authoritative
// del intento abierto (DEC-064) al corregir. Se mapea a camelCase donde
// aplica (`salidaEsperada`/`salidaObtenida`/`puntuacionObtenida`/
// `penalizacionPista`/`duracionSegundos`), mismo criterio que
// `diagram-execution-client.js#mapearResultadoEjecucion`.
//
// Errores propagados sin capturar (`ApiError`, ver `api-errors.js`): 401
// sin sesión ('unauthorized'), 403 sin CSRF válido ('forbidden'), 404
// ejercicio inexistente ('not_found'), 409 ejercicio no autocorregible --
// bloque pseudocodigo/python -- O (TSK-0176, DEC-064) sin ningún intento
// ABIERTO para este ejercicio (hay que llamar primero a
// `attempts-client.js#iniciarIntento`) -- ambos casos comparten el mismo
// `kind: 'conflict'`, ya que `ApiError` no transporta el detalle del cuerpo
// de la respuesta (ver `api-client.js`); `autocorrection-panel.js` usa un
// mensaje genérico correcto para los dos casos -- , 422 IR inválida
// ('validation_error'). Esta capa de servicio no decide cómo se muestra el
// error a la persona usuaria: esa decisión es de la UI que la invoque
// (TSK-0124/TSK-0176), igual criterio que `ejecutarDiagrama`/`guardarPreferenciaTema`.

import { apiRequest } from './api-client.js';

/** @typedef {number|string|boolean|null|Array<unknown>} ValorAutocorreccion */

/**
 * Los tres campos son TEXTO, no listas: así los declara
 * `ResultadoCasoAutocorreccion` en
 * `backend/src/app/schemas/autocorrection.py` (`entrada: str`,
 * `salida_esperada: str`, `salida_obtenida: str`, esta última ya formateada
 * de forma canónica por `domain/evaluacion_diagrama.formatear_salidas`).
 *
 * Este módulo los trataba como `ValorAutocorreccion[]` y los pasaba por un
 * `normalizarLista()` que devolvía `[]` para todo lo que no fuera array --
 * es decir, para SIEMPRE. Consecuencia observada en navegador: el panel de
 * corrección mostraba "(sin valores)" en entrada, salida esperada y salida
 * obtenida de todos los casos, en aciertos y en fallos, y la ayuda graduada
 * heredaba ese vacío ("tú das (sin valores) y se espera (sin valores)").
 * @typedef {{
 *   entrada: string,
 *   salidaEsperada: string,
 *   salidaObtenida: string,
 *   correcto: boolean,
 * }} CasoAutocorreccion
 */

/**
 * @typedef {{
 *   correcto: boolean,
 *   completado: boolean,
 *   intentos: number,
 *   casos: CasoAutocorreccion[],
 *   puntuacionObtenida: number | null,
 *   penalizacionPista: number,
 *   penalizacionReintento: number,
 *   duracionSegundos: number | null,
 *   yaNoPuntua: boolean,
 * }} ResultadoAutocorreccion
 */

/**
 * Normaliza un campo de texto crudo, de forma defensiva ante una respuesta
 * corrupta/parcial: cualquier valor que no sea ya una cadena se trata como
 * cadena vacía (mismo criterio que `pseudo-correction-client.js`, que ya
 * consumía correctamente estos mismos tres campos como texto).
 * @param {unknown} valor
 * @returns {string}
 */
function normalizarTexto(valor) {
  return typeof valor === 'string' ? valor : '';
}

/**
 * Mapea un caso crudo (snake_case) a `CasoAutocorreccion` (camelCase).
 * @param {unknown} item
 * @returns {CasoAutocorreccion}
 */
function mapearCaso(item) {
  const crudo = /** @type {Record<string, unknown>} */ (item ?? {});
  return {
    entrada: normalizarTexto(crudo.entrada),
    salidaEsperada: normalizarTexto(crudo.salida_esperada),
    salidaObtenida: normalizarTexto(crudo.salida_obtenida),
    correcto: Boolean(crudo.correcto),
  };
}

/**
 * Mapea la respuesta cruda de `POST /exercises/{id}/autocorrect`
 * (snake_case) al tipo interno de frontend (camelCase). Deliberadamente
 * defensivo con `unknown`, mismo criterio que
 * `diagram-execution-client.js#mapearResultadoEjecucion`.
 *
 * Campos nuevos (DEC-065, TSK-0176): `puntuacionObtenida`
 * (`puntuacion_obtenida`, `number | null` -- `null` si el ejercicio no tiene
 * `puntuacion_xp` fijada), `penalizacionPista` (`penalizacion_pista`,
 * cantidad de XP descontada por haber pedido pista en este intento, `0` si no
 * se pidió -- nunca acumulativo aunque se pidiera varias veces, DEC-063 punto
 * 3) y `duracionSegundos` (`duracion_segundos`, tiempo OFICIAL del intento
 * cerrado calculado por el servidor, `number | null`; distinto del cronómetro
 * visual de `autocorrection-panel.js`, que es solo informativo).
 * @param {unknown} datos
 * @returns {ResultadoAutocorreccion}
 */
export function mapearResultadoAutocorreccion(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    correcto: Boolean(crudo.correcto),
    completado: Boolean(crudo.completado),
    intentos: typeof crudo.intentos === 'number' ? crudo.intentos : 0,
    casos: Array.isArray(crudo.casos) ? crudo.casos.map(mapearCaso) : [],
    puntuacionObtenida: typeof crudo.puntuacion_obtenida === 'number' ? crudo.puntuacion_obtenida : null,
    penalizacionPista: typeof crudo.penalizacion_pista === 'number' ? crudo.penalizacion_pista : 0,
    // Descuento por repetir el ejercicio (20% del máximo por cada intento
    // anterior, sin suelo). Degrada a 0 si no viene: una respuesta antigua
    // se comporta como antes de existir la regla.
    penalizacionReintento:
      typeof crudo.penalizacion_reintento === 'number' ? crudo.penalizacion_reintento : 0,
    duracionSegundos: typeof crudo.duracion_segundos === 'number' ? crudo.duracion_segundos : null,
    // Tras esta corrección el ejercicio ya no suma puntos (completado o sin
    // intentos que puntúen). Solo `true` explícito cuenta.
    yaNoPuntua: crudo.ya_no_puntua === true,
  };
}

/**
 * Envía el programa (IR-JSON tal cual la produce `serializarPrograma`) del
 * ejercicio `ejercicioId` a autocorrección server-validada
 * (`POST /api/v1/exercises/{ejercicio_id}/autocorrect`). Requiere sesión
 * (401 si no la hay) y un token CSRF vigente (403 si falta o es inválido,
 * `opciones.csrfToken` -- ver `session-store.js#obtenerCsrfToken`; esta
 * función no lee el store directamente). Propaga `ApiError` sin capturarla
 * en cualquier caso (401/403/404/409/422/red/timeout): la UI que la invoque
 * (TSK-0124) decide cómo mostrar cada caso, esta capa de servicio no lo
 * decide.
 * @param {{
 *   ejercicioId: string,
 *   programa: Record<string, unknown>,
 *   csrfToken: string,
 *   signal?: AbortSignal,
 * }} opciones
 * @returns {Promise<ResultadoAutocorreccion>}
 */
export async function autocorregirDiagrama(opciones) {
  const { ejercicioId, programa, csrfToken, signal } = opciones ?? /** @type {any} */ ({});
  if (typeof ejercicioId !== 'string' || ejercicioId.trim() === '') {
    throw new TypeError('autocorregirDiagrama: "ejercicioId" debe ser un string no vacío');
  }
  if (typeof programa !== 'object' || programa === null || Array.isArray(programa)) {
    throw new TypeError('autocorregirDiagrama: "programa" debe ser un objeto IR-JSON (nodo Programa serializado)');
  }
  if (typeof csrfToken !== 'string' || csrfToken === '') {
    throw new TypeError('autocorregirDiagrama: "csrfToken" debe ser un string no vacío (token CSRF vigente)');
  }

  const datos = await apiRequest(`/exercises/${encodeURIComponent(ejercicioId)}/autocorrect`, {
    method: 'POST',
    body: { programa },
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrfToken },
    signal,
  });
  return mapearResultadoAutocorreccion(datos);
}
